import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { Ref, useEffect, useImperativeHandle, useRef } from 'react';

import { ProductType } from '@flipster/apis/v2/history/types';
import { positionHistoryColumns } from '@flipster/features/src/history-page/perpetual/position';
import PositionDesktopRow from '@flipster/features/src/history-page/perpetual/position/desktop-row';
import { FillDesktopRow } from '@flipster/features/src/history-page/shared/trade-history/fill-desktop-row';
import { getFillHistoryColumns } from '@flipster/features/src/history-page/shared/trade-history/fill-history';
import { OrderDesktopRow } from '@flipster/features/src/history-page/shared/trade-history/order-desktop-row';
import { getOrderHistoryColumns } from '@flipster/features/src/history-page/shared/trade-history/order-history';
import { Spinner } from '@flipster/react/design-system/spinner';
import { useDragScroll } from '@flipster/react/misc/browser/drag-scroll';
import { css } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';
import { Table, TableHeader } from '@flipster/ui/Table';

import { HistoryType, tradeHistoryBunja } from './state';

export interface TradeHistoryTableRef {
  scrollTop: () => void;
}

interface Props {
  ref: Ref<TradeHistoryTableRef>;
  tab: HistoryType;
}

export const TradeHistoryTable = ({ ref, tab }: Props) => {
  const {
    positionHistoryAtom,
    fillHistoryAtom,
    orderHistoryAtom,
    isTableLoadingAtom,
    positionHistoryQueryAtom,
    orderHistoryQueryAtom,
    fillHistoryQueryAtom,
    hasNextPageAtom,
    isFetchingNextPageAtom,
  } = useBunja(tradeHistoryBunja);

  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const mouseEvents = useDragScroll(scrollRef);

  const positionHistory = useAtomValue(positionHistoryAtom);
  const fillHistory = useAtomValue(fillHistoryAtom);
  const orderHistory = useAtomValue(orderHistoryAtom);
  const isLoading = useAtomValue(isTableLoadingAtom);
  const hasNextPage = useAtomValue(hasNextPageAtom);
  const isFetchingNextPage = useAtomValue(isFetchingNextPageAtom);

  const positionQuery = useAtomValue(positionHistoryQueryAtom);
  const orderQuery = useAtomValue(orderHistoryQueryAtom);
  const fillQuery = useAtomValue(fillHistoryQueryAtom);

  useImperativeHandle(ref, () => ({
    scrollTop: () => scrollRef.current?.scrollTo({ top: 0 }),
  }));

  // useIntersection은 내부에서 캐시하기 때문에 탭의 변화를 감지 못해서, 제대로 동작하지 않기 때문에 useEffect를 사용함
  useEffect(() => {
    const loadMoreElement = loadMoreRef.current;
    if (!loadMoreElement || !hasNextPage || isFetchingNextPage) return;

    try {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            if (tab === 'position') {
              positionQuery.fetchNextPage();
            } else if (tab === 'order') {
              orderQuery.fetchNextPage();
            } else if (tab === 'fill') {
              fillQuery.fetchNextPage();
            }
          }
        },
        { threshold: 0.1 },
      );

      observer.observe(loadMoreElement);

      return () => {
        observer.disconnect();
      };
    } catch (err) {
      // oxlint-disable-next-line no-console
      console.error(err);
    }
  }, [tab, hasNextPage, isFetchingNextPage, positionQuery, orderQuery, fillQuery]);

  const tableClassName = css({ '& table': { width: 'max-content', minW: '100%' } });

  return (
    <div
      ref={scrollRef}
      {...mouseEvents}
      className={css({ pos: 'relative', overflowX: 'auto', overflowY: 'auto', flex: 1, minH: 0 })}
    >
      {tab === 'position' && (
        <Table
          className={tableClassName}
          tHeader={
            <TableHeader bold={false} columns={positionHistoryColumns} className={css({ '& th': { pt: '0!' } })} />
          }
          items={positionHistory || []}
          renderRow={(item) => <PositionDesktopRow key={item.positionId} item={item} />}
          isLoading={isLoading}
        />
      )}
      {tab === 'fill' && (
        <Table
          className={tableClassName}
          tHeader={
            <TableHeader
              bold={false}
              columns={getFillHistoryColumns(ProductType.Perpetual)}
              className={css({ '& th': { pt: '0!' } })}
            />
          }
          items={fillHistory || []}
          renderRow={(item) => (
            <FillDesktopRow item={item} key={`row-${item.execId}`} productType={ProductType.Perpetual} />
          )}
          isLoading={isLoading}
        />
      )}
      {tab === 'order' && (
        <Table
          className={tableClassName}
          tHeader={
            <TableHeader
              bold={false}
              columns={getOrderHistoryColumns(ProductType.Perpetual)}
              className={css({ '& th': { pt: '0!' } })}
            />
          }
          items={orderHistory || []}
          renderRow={(item) => (
            <OrderDesktopRow item={item} key={`row-${item.orderId}`} productType={ProductType.Perpetual} />
          )}
          isLoading={isLoading}
        />
      )}
      {hasNextPage && (
        <div ref={loadMoreRef} className={center({ py: 16 })}>
          <Spinner size={24} />
        </div>
      )}
    </div>
  );
};

export default TradeHistoryTable;
